import { apiClient } from '@api'

/**
 * Checklist Service
 *
 * Handles checklist items management for projects
 */

const checklistService = {
	/**
	 * Get all checklist items for a project
	 * @param {number} projectId - Project ID
	 * @param {Object} filters - Optional filters
	 * @returns {Promise} Checklist items
	 */
	async getByProject(projectId, filters = {}) {
		const params = { project_id: projectId, ...filters }
		const queryString = new URLSearchParams(params).toString()
		return apiClient.get(`/checklists?${queryString}`)
	},

	/**
	 * Get checklist item by ID
	 * @param {number} id - Checklist item ID
	 * @returns {Promise} Checklist item
	 */
	async getById(id) {
		return apiClient.get(`/checklists/${id}`)
	},

	/**
	 * Create single checklist item
	 * @param {Object} data - Checklist item data
	 * @returns {Promise} Created item
	 */
	async create(data) {
		return apiClient.post('/checklists', data)
	},

	/**
	 * Update checklist item
	 * @param {number} id - Item ID
	 * @param {Object} data - Updated data
	 * @returns {Promise} Updated item
	 */
	async update(id, data) {
		return apiClient.put(`/checklists/${id}`, data)
	},

	/**
	 * Delete checklist item
	 * @param {number} id - Item ID
	 * @returns {Promise} Deletion result
	 */
	async delete(id) {
		return apiClient.delete(`/checklists/${id}`)
	},

	/**
	 * Approve a manually created checklist item (legacy — Company Admin / Super Admin)
	 * @param {number} id - Checklist ID
	 * @param {string} notes - Optional approval notes
	 * @returns {Promise} Updated item
	 */
	async approve(id, notes = '') {
		return apiClient.put(`/checklists/${id}/approve`, { notes })
	},

	/**
	 * Reject a manually created checklist item (legacy — Company Admin / Super Admin)
	 * @param {number} id - Checklist ID
	 * @param {string} reason - Rejection reason
	 * @returns {Promise} Updated item
	 */
	async reject(id, reason = '') {
		return apiClient.put(`/checklists/${id}/reject`, { reason })
	},

	/**
	 * PM reviews a checklist item (approve or reject)
	 * @param {number} id - Checklist ID
	 * @param {'approve'|'reject'} action
	 * @param {string} notes - Notes / rejection reason
	 * @returns {Promise} Updated item
	 */
	async pmReview(id, action, notes = '') {
		return apiClient.put(`/checklists/${id}/pm-review`, { action, notes })
	},

	/**
	 * Management makes final decision on a checklist item the PM rejected
	 * @param {number} id - Checklist ID
	 * @param {'approve'|'reject'} action
	 * @param {string} notes - Decision notes
	 * @returns {Promise} Updated item
	 */
	async managementReview(id, action, notes = '') {
		return apiClient.put(`/checklists/${id}/management-review`, { action, notes })
	},

	/**
	 * Management revokes a previously approved checklist item
	 * @param {number} id - Checklist ID
	 * @param {string} notes - Reason for revocation
	 * @returns {Promise} Updated item
	 */
	async revoke(id, notes = '') {
		return apiClient.put(`/checklists/${id}/revoke`, { notes })
	},

	/**
	 * Batch create checklist items
	 * @param {number} projectId - Project ID
	 * @param {Array} items - Array of checklist items
	 * @param {boolean} isAiGenerated - Whether items are AI generated
	 * @returns {Promise} Created items
	 */
	async batchCreate(projectId, items, isAiGenerated = false) {
		return apiClient.post('/checklists/batch', {
			project_id: projectId,
			items,
			is_ai_generated: isAiGenerated,
		})
	},

	/**
	 * Batch update checklist items
	 * @param {Array} items - Array of items with IDs
	 * @returns {Promise} Update result
	 */
	async batchUpdate(items) {
		return apiClient.put('/checklists/batch-update', { items })
	},

	/**
	 * Reorder checklist items
	 * @param {number} projectId - Project ID
	 * @param {Array} orderedIds - Array of IDs in new order
	 * @returns {Promise} Reorder result
	 */
	async reorder(projectId, orderedIds) {
		return apiClient.put('/checklists/reorder', {
			project_id: projectId,
			ordered_ids: orderedIds,
		})
	},

	/**
	 * Get checklist statistics
	 * @param {number} projectId - Project ID
	 * @returns {Promise} Statistics data
	 */
	async getStats(projectId) {
		return apiClient.get(`/projects/${projectId}/checklists/stats`)
	},

	/**
	 * Get overdue items
	 * @param {number} projectId - Project ID
	 * @returns {Promise} Overdue items
	 */
	async getOverdue(projectId) {
		return apiClient.get(`/projects/${projectId}/checklists/overdue`)
	},

	/**
	 * Get workflow status for a checklist item
	 * @param {number} id - Checklist item ID
	 * @returns {Promise} Workflow status data
	 */
	async getWorkflowStatus(id) {
		return apiClient.get(`/checklists/${id}/workflow-status`)
	},

	/**
	 * Advance checklist workflow to next step
	 * @param {number} id - Checklist item ID
	 * @param {Object} data - Completion data { notes, evidence_attached }
	 * @returns {Promise} Updated workflow status
	 */
	async advanceWorkflow(id, data) {
		return apiClient.post(`/checklists/${id}/advance-workflow`, data)
	},

	/**
	 * Get workflow status options
	 * @returns {Array} Status options with labels
	 */
	getWorkflowStatuses() {
		return [
			{ value: 'not_started', label: 'Not Started', color: 'gray' },
			{ value: 'in_progress', label: 'In Progress', color: 'blue' },
			{ value: 'pending_review', label: 'Pending Review', color: 'yellow' },
			{ value: 'approved', label: 'Approved', color: 'green' },
			{ value: 'rejected', label: 'Rejected', color: 'red' },
			{ value: 'not_applicable', label: 'Not Applicable', color: 'gray' },
		]
	},

	/**
	 * Get status label
	 * @param {string} status - Status key
	 * @returns {string} Human-readable label
	 */
	getStatusLabel(status) {
		// Default to 'not_started' if status is null/undefined/empty
		const normalizedStatus = status || 'not_started'
		const statuses = this.getWorkflowStatuses()
		const found = statuses.find((s) => s.value === normalizedStatus)
		return found ? found.label : 'Not Started'
	},

	/**
	 * Get status color
	 * @param {string} status - Status key
	 * @returns {string} Color class
	 */
	getStatusColor(status) {
		// Default to 'not_started' if status is null/undefined/empty
		const normalizedStatus = status || 'not_started'
		const statuses = this.getWorkflowStatuses()
		const found = statuses.find((s) => s.value === normalizedStatus)
		return found ? found.color : 'gray'
	},

	/**
	 * Calculate completion percentage
	 * @param {Array} items - Checklist items
	 * @returns {number} Completion percentage
	 */
	calculateCompletion(items) {
		if (!items || items.length === 0) return 0

		const completedStatuses = ['approved', 'not_applicable']
		const totalWeight = items.reduce((sum, item) => sum + (item.weightage || 1), 0)
		const completedWeight = items
			.filter((item) => completedStatuses.includes(item.workflow_status))
			.reduce((sum, item) => sum + (item.weightage || 1), 0)

		return totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0
	},

	/**
	 * Group items by certificate
	 * @param {Array} items - Checklist items
	 * @returns {Object} Items grouped by certificate_id
	 */
	groupByCertificate(items) {
		return items.reduce((groups, item) => {
			const certId = item.certificate_id || 'uncategorized'
			if (!groups[certId]) {
				groups[certId] = []
			}
			groups[certId].push(item)
			return groups
		}, {})
	},

	/**
	 * Group items by workflow status
	 * @param {Array} items - Checklist items
	 * @returns {Object} Items grouped by status
	 */
	groupByStatus(items) {
		return items.reduce((groups, item) => {
			const status = item.workflow_status || 'not_started'
			if (!groups[status]) {
				groups[status] = []
			}
			groups[status].push(item)
			return groups
		}, {})
	},
}

export default checklistService
